import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import PolicyNav, { POLICY_UPDATED } from "./PolicyNav";
import "../css/privacy.scss";

const documents = {
  orders: {
    path: "/orders",
    eyebrow: "Customer care",
    title: "Order processing policy",
    description:
      "How an order moves from checkout through verification, acceptance, fulfilment, and dispatch.",
    introTitle: "A receipt is the start, not the finish.",
    intro: (
      <>
        Submitting checkout sends us an offer to purchase. An automated receipt
        confirms that the request reached us; an order is accepted when we send
        a dispatch confirmation or otherwise confirm acceptance.
      </>
    ),
    sections: [
      {
        title: "Information you provide",
        paragraphs: [
          "Provide a current email address, recipient name, complete delivery address, and any other information requested at checkout. Review variants, quantities, spelling, and totals before payment. We are not responsible for avoidable delay caused by incomplete or inaccurate information, but we will try to help if you contact us promptly.",
        ],
      },
      {
        title: "Verification & payment review",
        paragraphs: [
          "We may verify payment status, delivery details, product limits, and indicators of fraud or unauthorized use. We may contact you for proportionate information needed to complete that review. An authorization or pending bank entry is not proof that an order has been accepted.",
          "Never email a full payment-card number, security code, password, or government identifier in response to a support request.",
        ],
      },
      {
        title: "Availability, limits & acceptance",
        paragraphs: [
          "Inventory can change between adding an item to your bag and order review. We may limit quantities or reject an order for unavailability, delivery restrictions, an obvious price or description error, suspected fraud, unauthorized resale, breach of purchase limits, or another lawful reason. If we cannot accept a paid order, we will release or refund the affected amount.",
        ],
      },
      {
        title: "Processing, split orders & substitutions",
        paragraphs: [
          "Normal processing estimates appear in the Shipping & Delivery Policy. We may send products separately when that helps fulfil the order, without increasing the shipping amount already agreed unless you approve a change. We will not substitute a materially different product without your consent.",
        ],
      },
      {
        title: "Status, records & support",
        paragraphs: [
          "Keep your order reference and confirmation messages. Status messages may be delayed or filtered by your email provider. If you have not received a receipt after payment, check spam or junk folders and then contact customer care with the email used at checkout and any non-sensitive payment reference.",
        ],
      },
    ],
  },
  preorders: {
    path: "/preorders",
    eyebrow: "Shopping terms",
    title: "Pre-orders & backorders",
    description:
      "Additional terms for products clearly identified as pre-order, made-to-order, or temporarily backordered.",
    introTitle: "Only when the product page says so.",
    intro: (
      <>
        A normal in-stock product is not a pre-order. These terms apply only
        when the product page or checkout clearly labels an item as a pre-order,
        made-to-order item, or backorder before you pay.
      </>
    ),
    sections: [
      {
        title: "Estimated availability",
        paragraphs: [
          "Any release, dispatch, or restock date is a good-faith estimate rather than a guaranteed date. Production, inspection, inbound freight, customs, or supplier disruption may change it. Delivery transit begins only after dispatch.",
        ],
      },
      {
        title: "Payment timing",
        paragraphs: [
          "The product page or checkout will state whether payment is collected at order time or later. Your bank may show an authorization separately. We will not introduce a recurring charge merely because you placed a pre-order.",
        ],
      },
      {
        title: "Mixed baskets",
        paragraphs: [
          "When an order contains in-stock and pre-order items, we may hold the order until all items are ready or dispatch separately. The arrangement shown at checkout or in your confirmation controls. We will request approval before adding a new shipping charge.",
        ],
      },
      {
        title: "Changes, cancellation & material delay",
        paragraphs: [
          "Contact us promptly to request a change or cancellation. A customized or already committed production order may not be cancellable except where law provides otherwise. If a material delay occurs, we will provide an updated estimate and any cancellation or refund option required by applicable law.",
        ],
      },
      {
        title: "Returns & product changes",
        paragraphs: [
          "Unless a lawful exception is disclosed before purchase, an eligible pre-order follows the Returns & Refunds Policy after delivery. Minor refinements that do not materially change the product may occur before production; we will seek consent or offer an appropriate remedy for a material change.",
        ],
      },
    ],
  },
  subscriptions: {
    path: "/subscriptions",
    eyebrow: "Shopping terms",
    title: "Subscription terms",
    description:
      "The status of recurring purchases and the protections that would apply before any subscription is offered.",
    introTitle: "No recurring product subscription is offered today.",
    intro: (
      <>
        The current storefront sells one-time purchases. Adding an item to the
        bag, checking out, or contacting support does not enroll you in an
        automatically renewing product subscription.
      </>
    ),
    sections: [
      {
        title: "Clear consent before enrollment",
        paragraphs: [
          "If subscriptions are introduced, the product page and checkout will clearly disclose the product or service, price, billing interval, minimum commitment if any, renewal method, shipping charge, cancellation method, and other material terms before consent. A pre-selected option will not replace affirmative consent where law requires it.",
        ],
      },
      {
        title: "Order confirmation & renewal notices",
        paragraphs: [
          "A subscriber would receive a durable confirmation of the recurring terms and a practical way to retain them. Renewal or price-change notices would be sent when and as required by applicable law, using the contact details provided by the subscriber.",
        ],
      },
      {
        title: "Manage or cancel",
        paragraphs: [
          "Any future subscription would include a reasonably simple cancellation method that is at least as easy to use as the enrollment method where required. Cancellation would stop future renewals after the effective date but would not automatically reverse an order already processed or dispatched.",
        ],
      },
      {
        title: "Price, availability & failed payment",
        paragraphs: [
          "Material price or program changes would receive advance notice and consent where required. If a payment fails or a product becomes unavailable, we could pause, skip, or cancel the affected renewal after reasonable notice rather than silently substituting a materially different item.",
        ],
      },
      {
        title: "Policy activation",
        paragraphs: [
          "This page is a disclosure of current status, not an offer of a subscription. We will revise it with complete program-specific terms before enabling recurring purchases. Mandatory cancellation and cooling-off rights will continue to apply.",
        ],
      },
    ],
  },
  communications: {
    path: "/communications",
    eyebrow: "Privacy & preferences",
    title: "Email & SMS communications",
    description:
      "The difference between order messages and marketing, plus your communication choices.",
    introTitle: "Service messages are not marketing messages.",
    intro: (
      <>
        We may use the contact details you provide to complete an order, answer
        a request, provide safety information, or prevent fraud. Promotional
        messages require the permission or other lawful basis applicable where
        you live.
      </>
    ),
    sections: [
      {
        title: "Transactional communications",
        paragraphs: [
          "Order receipts, payment or fraud-review notices, dispatch updates, return messages, security alerts, and product-safety notices relate to a transaction or requested service. You may continue to receive necessary service messages even after opting out of marketing.",
        ],
      },
      {
        title: "Marketing email",
        paragraphs: [
          "Where marketing email is offered, you can unsubscribe through the link in the message or by contacting us. An opt-out may take a short time to process, but we will act within the period required by law. We may retain a minimal suppression record so we do not add the address back accidentally.",
        ],
      },
      {
        title: "SMS status",
        paragraphs: [
          "The current storefront does not operate a promotional SMS subscription. Providing a telephone number for delivery or support does not consent to marketing texts. If an SMS program is introduced, its sender, message purpose, frequency, automated-technology use, STOP and HELP instructions, and any carrier charges will be disclosed before enrollment.",
        ],
      },
      {
        title: "Consent, charges & eligibility",
        paragraphs: [
          "Consent to marketing is not a condition of purchase. Your carrier, email provider, or internet provider may charge for data or messaging under your own plan. You must be authorized to use the email address or telephone number you provide.",
        ],
      },
      {
        title: "Phishing & suspicious messages",
        paragraphs: [
          "We will not ask by email or text for your full card number, card security code, password, or payment by gift card or cryptocurrency. Do not open unexpected attachments or follow suspicious payment links. Forward the message details to customer care for review.",
        ],
      },
    ],
  },
  reviews: {
    path: "/reviews",
    eyebrow: "Community terms",
    title: "Reviews & user content",
    description:
      "Rules for reviews, photographs, questions, and other material voluntarily submitted for publication.",
    introTitle: "Share your experience, not someone else’s work.",
    intro: (
      <>
        These terms apply when a feature or invitation lets you submit content
        for possible publication. Sending an ordinary private support message
        does not give us permission to publish it.
      </>
    ),
    sections: [
      {
        title: "Your promises",
        paragraphs: [
          "You confirm that your submission reflects a genuine experience, is accurate to the best of your knowledge, and is content you own or have permission to use. Do not include another person’s private information, confidential material, unlawful content, malware, undisclosed advertising, or content that infringes intellectual-property or other rights.",
        ],
      },
      {
        title: "Moderation",
        paragraphs: [
          "We may decline, redact, label, or remove content that is off topic, duplicative, abusive, misleading, unsafe, unlawful, technically harmful, or inconsistent with these rules. We will not promise to publish every submission or to keep it available indefinitely. Moderation does not mean we endorse a submission.",
        ],
      },
      {
        title: "Permission to use published content",
        paragraphs: [
          "For content you intentionally submit for publication, you grant us a non-exclusive, worldwide, royalty-free license to host, reproduce, format, translate, and display it in connection with this store, its products, and customer support. The license lasts only as long as reasonably needed for those purposes and any required records. You keep your ownership rights.",
        ],
      },
      {
        title: "Names, images & privacy",
        paragraphs: [
          "Use only the public display name requested by the form. Do not submit an image of another identifiable person without permission. Avoid showing shipping labels, documents, faces, children, or other sensitive details. Personal information remains subject to the Privacy Policy.",
        ],
      },
      {
        title: "Incentives, corrections & complaints",
        paragraphs: [
          "If a benefit is offered for a review, the invitation and published review should clearly disclose it; the benefit is not conditioned on a positive opinion. Contact us to correct or request removal of your submission, report impersonation, or identify suspected infringement. Legal retention and third-party reposts may limit complete removal.",
        ],
      },
    ],
  },
  security: {
    path: "/security",
    eyebrow: "Trust & safety",
    title: "Security & fraud prevention",
    description:
      "How the storefront approaches payment security, order review, scams, and responsible reporting.",
    introTitle: "Protect the order without collecting more than needed.",
    intro: (
      <>
        Security is a shared practice. We use proportionate safeguards and
        service providers to operate the store, while customers should protect
        their devices, email accounts, order references, and payment details.
      </>
    ),
    sections: [
      {
        title: "Payment security",
        paragraphs: [
          "Full card details are entered on the hosted payment provider’s page rather than this storefront. We receive payment status and limited transaction details needed to process an order, issue a refund, reconcile records, and investigate fraud. No internet transmission or storage system can be guaranteed completely secure.",
        ],
      },
      {
        title: "Fraud review",
        paragraphs: [
          "We or our providers may evaluate transaction, device, address, velocity, and payment signals to prevent unauthorized purchases and abuse. We may pause, verify, limit, reject, or cancel an order when reasonably necessary. A review is not an accusation and decisions remain subject to applicable law.",
        ],
      },
      {
        title: "Customer precautions",
        items: [
          "Use a trusted device and connection for checkout.",
          "Keep order confirmations and gift-card codes private.",
          "Check the website address before entering payment information.",
          "Contact your payment provider promptly about an unauthorized charge.",
          "Do not send passwords or complete payment-card details to customer care.",
        ],
      },
      {
        title: "Responsible security reports",
        paragraphs: [
          "If you believe you found a security issue, email a clear description, affected page, reproduction steps, and non-sensitive evidence. Do not access another person’s data, disrupt service, use automated high-volume testing, alter or delete data, demand payment, or publicly disclose an unresolved issue. This contact does not create a bug-bounty promise or authorize testing that would otherwise be unlawful.",
        ],
      },
      {
        title: "Incidents & account recovery",
        paragraphs: [
          "We will investigate credible reports and provide notices required by law. If your email or payment account may be compromised, secure it with the relevant provider first, then contact us with your order reference. We may ask for proportionate verification before discussing or changing an order.",
        ],
      },
    ],
  },
  wholesale: {
    path: "/wholesale",
    eyebrow: "Business purchasing",
    title: "Wholesale & corporate orders",
    description:
      "Baseline terms for bulk, corporate gifting, resale, or other separately quoted business purchases.",
    introTitle: "A conversation is not yet a purchase order.",
    intro: (
      <>
        Wholesale and corporate orders are considered case by case. An enquiry,
        estimate, sample, or draft invoice is not binding until both parties
        confirm the products, quantity, price, timing, delivery, and payment terms.
      </>
    ),
    sections: [
      {
        title: "Quotes & availability",
        paragraphs: [
          "A quote is valid only for the period written on it and depends on inventory, production capacity, destination, and any minimum quantity. Retail promotions and discount codes do not apply unless the quote says they do. Taxes, freight, duties, customization, and samples may be priced separately.",
        ],
      },
      {
        title: "Approval, payment & changes",
        paragraphs: [
          "We may require a deposit, full advance payment, purchase order, tax documentation, or credit approval. Production or allocation begins only after the stated approvals and cleared payment. Change or cancellation requests may create reasonable costs for committed materials, labor, personalization, or non-refundable logistics.",
        ],
      },
      {
        title: "Lead times, delivery & inspection",
        paragraphs: [
          "Business-order lead times are estimates unless the written agreement expressly guarantees a date. The buyer must provide delivery constraints and inspect the shipment promptly. Shortage, visible damage, or incorrect goods should be reported within the period stated in the quote, without limiting mandatory rights.",
        ],
      },
      {
        title: "Returns & customized goods",
        paragraphs: [
          "Retail return terms do not automatically apply to a negotiated business order. Custom, personalized, specially sourced, or made-to-order goods are generally not returnable for change of mind. Defective, damaged, or misdescribed goods remain eligible for the remedy required by the agreement and applicable law.",
        ],
      },
      {
        title: "Resale, branding & compliance",
        paragraphs: [
          "Resale is permitted only when confirmed in writing. A buyer may not alter products, remove safety or origin information, register confusingly similar domains or marks, imply an agency relationship, or use brand assets beyond the permission granted. Each party remains responsible for its tax, advertising, sanctions, import, product, and consumer-law obligations.",
        ],
      },
    ],
  },
  marketplaces: {
    path: "/marketplaces",
    eyebrow: "Buying safely",
    title: "Third-party sales channels",
    description:
      "How support, authenticity, returns, and promotions work when a purchase is made away from this website.",
    introTitle: "The seller shown on the receipt matters.",
    intro: (
      <>
        This website is an official {siteConfig.brandName} sales channel. A
        marketplace, retailer, social account, or reseller should not be treated
        as authorized unless we identify it as such or confirm it in writing.
      </>
    ),
    sections: [
      {
        title: "Purchases from another seller",
        paragraphs: [
          "A purchase made from an independent seller is governed by that seller’s checkout, delivery, return, privacy, and payment terms. Contact the seller shown on the receipt first. We may provide product or safety information, but we cannot issue a refund for money we did not receive.",
        ],
      },
      {
        title: "Authenticity & proof of purchase",
        paragraphs: [
          "We cannot guarantee the authenticity, condition, storage, history, or legal sale of an item obtained from an unauthorized seller. Keep the original receipt, listing, packaging, and seller messages. We may request them when reviewing a product or safety concern.",
        ],
      },
      {
        title: "Pricing, promotions & warranties",
        paragraphs: [
          "Independent sellers set their own prices and promotions. A website offer, discount code, gift card, return benefit, or commercial warranty does not automatically apply elsewhere. Mandatory product and consumer rights may still apply according to the relevant law and seller relationship.",
        ],
      },
      {
        title: "Impersonation & payment scams",
        paragraphs: [
          "Be cautious of accounts that request payment by gift card, cryptocurrency, wire transfer, or an unfamiliar link; promise unrealistic discounts; or ask for full card details by message. Report suspected impersonation with the exact account name, URL, screenshots, and platform details. Contact your payment provider immediately if money was sent.",
        ],
      },
      {
        title: "Unauthorized use of brand material",
        paragraphs: [
          <>A third party may not copy store photography, product text, or branding in a way that misleads customers or infringes rights. Rights holders can use the <Link to="/intellectual-property">Intellectual Property Notice</Link> to submit a detailed report.</>,
        ],
      },
    ],
  },
};

function CommercePolicy({ type }) {
  const document = documents[type];

  return (
    <main className="policy-page privacy">
      <header className="policy-hero policy-hero--legal">
        <p className="policy-eyebrow">{document.eyebrow}</p>
        <h1>{document.title}</h1>
        <p>{document.description}</p>
        <span>Effective {POLICY_UPDATED}</span>
      </header>

      <div className="policy-layout">
        <PolicyNav current={document.path} />

        <article className="policy-content policy-content--legal">
          <section className="policy-intro" aria-labelledby={`${type}-overview`}>
            <p className="policy-eyebrow">Overview</p>
            <h2 id={`${type}-overview`}>{document.introTitle}</h2>
            <p>{document.intro}</p>
          </section>

          {document.sections.map((section, index) => {
            const headingId = `${type}-section-${index + 1}`;

            return (
              <section
                className="policy-section"
                aria-labelledby={headingId}
                key={section.title}
              >
                <div className="policy-section__heading">
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <h2 id={headingId}>{section.title}</h2>
                </div>
                {section.paragraphs?.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex}>{paragraph}</p>
                ))}
                {section.items && (
                  <ul>
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}

          <section className="policy-help" aria-labelledby={`${type}-help`}>
            <div>
              <p className="policy-eyebrow">Questions?</p>
              <h2 id={`${type}-help`}>Ask customer care before you proceed.</h2>
            </div>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email} <span aria-hidden="true">↗</span>
            </a>
          </section>
        </article>
      </div>
    </main>
  );
}

export const OrdersPolicy = () => <CommercePolicy type="orders" />;
export const PreordersPolicy = () => <CommercePolicy type="preorders" />;
export const SubscriptionsPolicy = () => <CommercePolicy type="subscriptions" />;
export const CommunicationsPolicy = () => <CommercePolicy type="communications" />;
export const ReviewsPolicy = () => <CommercePolicy type="reviews" />;
export const SecurityPolicy = () => <CommercePolicy type="security" />;
export const WholesalePolicy = () => <CommercePolicy type="wholesale" />;
export const MarketplacesPolicy = () => <CommercePolicy type="marketplaces" />;

